import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import store from "./redux/store";
import "./App.css";
import "./common.scss";
import IntroPage, { CenteredBox } from "./components/introPage";
import NotFound from "./components/404";
import PrivacyPolicy from "./views/policy/privacy-policy";
import RefundPolicy from "./views/policy/refund";
import TermsConditions from "./views/policy/terms";
import CommonDetails from "./views/common/commonDetails";
import Updates from "./components/updates";
import Login from "./components/login";
import Event from "./components/event";
import Notice from "./components/notice";
import SevaList from "./components/sevaList";
import Punyarjak from "./components/punyarjak";
import SevaBooking from "./components/sevaBooking";
import BookingDetail from "./components/bookingDetail";
import Payment from "./components/DonationPayment/payment";
import { PaymentProvider } from "./components/DonationPayment/paymentContext";
import AboutUs from "./components/aboutUs";
import Gallery from "./components/galleries";
import Idol from "./components/idol/idol";
import DonationPage from "./components/donationPage";
import PaymentResponsePage from "./components/PaymentResponsePage";
import BellOverlay from "./components/idol/idolAnimation/bellOverlay";
import ApplicationList from "./components/applications/applicationList";
import ApplicationFormViewParent from "./components/applications/applicationFormViewParent";
import PublicDonationForm from "./components/publicDonationForm";
import NameOverlayApp from "./components/nameOverlayApp";
import { getTrustDetail, loginPage } from "./utility/commonApi";
import { logOut, setTrustData } from "./redux/slices/authSlice";
import Loader from "./views/common/loader";
import { Spinner } from "reactstrap";

function AppRoutes() {
  const dispatch = useDispatch();
  const location = useLocation();

  const userDetails = useSelector((state) => state.auth);
  const user = userDetails ? userDetails.user : null;

  const isServicesEnabled = userDetails?.trustData?.isSevaEnabled ?? false;
  const donationViaPortfolioApp =
    userDetails?.trustData?.donationViaPortfolioApp ?? false;
  const isApplicationEnabled =
    userDetails?.trustData?.isApplicationEnabled ?? false;
  const hasDharmshala = userDetails?.trustData?.hasDharmshala ?? false;
  const trustData = userDetails && userDetails?.trustData;
  const idols = trustData?.idols || [];
  const isChaturmasMode = localStorage.getItem("logoutRedirectUrl")?.includes("bangalore-chaturmas");

  const canLogin =
    !trustData || // If trustData is missing (e.g. after logout), allow Login to render and fetch it
    isServicesEnabled ||
    hasDharmshala ||
    donationViaPortfolioApp ||
    isApplicationEnabled;

  const hasAnyFeatureEnabled =
    isServicesEnabled ||
    isApplicationEnabled ||
    hasDharmshala ||
    donationViaPortfolioApp;
  useEffect(() => {
    if (!hasAnyFeatureEnabled && user) {
      dispatch(logOut());
    }
  }, [hasAnyFeatureEnabled, user, dispatch]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const homeUrl = queryParams.get("homeUrl");
    if (homeUrl) {
      localStorage.setItem("logoutRedirectUrl", homeUrl);
    }
  }, [location.search]);

  return (
    <Routes>
      <Route
        path="/"
        element={user ? <Navigate to="/home" replace /> : <IntroPage />}
      />{" "}
      <Route
        path="/idol"
        element={
          !isChaturmasMode && idols && idols.length > 0 ? <Idol /> : <Navigate to="/" replace />
        }
      />
      <Route path="/about" element={!isChaturmasMode ? <AboutUs /> : <Navigate to="/" replace />} />
      <Route path="/donation" element={!isChaturmasMode ? <DonationPage /> : <Navigate to="/" replace />} />
      <Route path="/galleries" element={!isChaturmasMode ? <Gallery /> : <Navigate to="/" replace />} />
      <Route path="/updates" element={!isChaturmasMode ? <Updates /> : <Navigate to="/" replace />} />
      <Route path="/events" element={!isChaturmasMode ? <Event /> : <Navigate to="/" replace />} />
      <Route path="/notices" element={!isChaturmasMode ? <Notice /> : <Navigate to="/" replace />} />
      <Route
        path="/applications"
        element={
          isApplicationEnabled ? (
            !user ? (
              // Not logged in → send to login
              <Navigate
                to="/login"
                replace
                state={{ from: "/home?page=applications" }}
              />
            ) : (
              // Logged in → always go to /home?page=applications
              <Navigate to="/home?page=applications" replace />
            )
          ) : (
            // Application disabled
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="/applications/:id" element={<ApplicationFormViewParent />} />
      <Route
        path="/seva"
        element={isServicesEnabled ? <SevaList /> : <Navigate to="/" replace />}
      />
      {/* Conditional Route for /seva */}
      <Route
        path="/home"
        element={
          !user ? (
            <Navigate
              to="/login"
              replace
              state={{ from: location.pathname + location.search }} // dynamic from
            />
          ) : hasAnyFeatureEnabled ? (
            <SevaList />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="/punyarjak" element={!isChaturmasMode ? <Punyarjak /> : <Navigate to="/" replace />} />
      <Route path="/detail/:type/:id" element={<CommonDetails />} />
      <Route path="/seva-book/:id" element={<SevaBooking />} />
      <Route path="/booking/detail/:bookingId" element={<BookingDetail />} />
      <Route path="/Payment" element={<Payment />} />
      <Route path="/payment-response" element={<PaymentResponsePage />} />
      <Route
        path="/login"
        element={canLogin ? <Login /> : <Navigate to="/" replace />}
      />
      <Route path="/trust-donation" element={!isChaturmasMode ? <PublicDonationForm /> : <Navigate to="/" replace />} />
      <Route
        path="/certificate"
        element={
          <NameOverlayApp
            onDataSubmit={async (userData) => {
              await fetch(
                "https://script.google.com/macros/s/AKfycbz8ncHh1By9Wje1SbjT91kLJdZ3RfjUUmiQlur9k-rEvRUF4wQzapzh3yK4eLPWdNUj/exec",
                {
                  method: "POST",
                  mode: "no-cors",
                  body: JSON.stringify(userData),
                }
              );
              return { success: true };
            }}
          />
        }
      />
      <Route path="*" element={<NotFound />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
      <PaymentProvider>
        <BrowserRouter>
          {window.innerWidth > 768 && <BellOverlay />}
          <TrustDataWrapper>
            <AppRoutes />
          </TrustDataWrapper>
        </BrowserRouter>
      </PaymentProvider>
    </Provider>
  );
}

export default App;
function TrustDataWrapper({ children }) {
  const dispatch = useDispatch();
  const trustData = useSelector((state) => state.auth.trustData);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const initTrustData = async () => {
      try {
        let trustId = localStorage.getItem("trustId");

        if (!trustId) {
          // subdomain se trustId fetch karo
          const hostname = window.location.hostname;
          const subDomainName = hostname.replace(
            process.env.REACT_APP_ADMIN_SUBDOMAIN_REPLACE_URL,
            ""
          );
          const res = await loginPage(subDomainName);
          trustId = res?.data?.result?.id;
          if (trustId) {
            localStorage.setItem("trustId", trustId);
          }
        }

        if (trustId) {
          const res = await getTrustDetail(trustId);
          dispatch(setTrustData(res?.data?.result));
        }
      } catch (err) {
        console.error("Failed to load trustData", err);
      } finally {
        setLoading(false);
      }
    };

    initTrustData();
  }, [dispatch]);

  if (loading) {
    return (
      <CenteredBox height="100dvh">
        <Spinner style={{ height: "3rem ", width: "3rem", color: "#ff8744" }} />
      </CenteredBox>
    );
  }

  return children;
}
